import { getIn, setIn, useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isEqual } from "../util";

const isNestedValue = value =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Date);

const getChangedFieldPaths = (values, initialValues, prefix = "") => {
  if (isEqual(values, initialValues)) return [];

  if (!isNestedValue(values) || !isNestedValue(initialValues)) {
    return prefix ? [prefix] : [];
  }

  const keys = [
    ...new Set([...Object.keys(values), ...Object.keys(initialValues)]),
  ];

  return keys.flatMap(key => {
    const path = prefix ? `${prefix}.${key}` : key;
    return getChangedFieldPaths(values?.[key], initialValues?.[key], path);
  });
};

const preserveValuesAtPaths = (nextValues, currentValues, paths) =>
  paths.reduce(
    (values, path) => setIn(values, path, getIn(currentValues, path)),
    nextValues,
  );

const useAutosaveFormik = ({
  autosaveDelay = 2000,
  initialValues,
  savedStateDuration = 1000,
  onSave,
  ...formikOptions
}) => {
  const formik = useFormik({
    ...formikOptions,
    initialValues,
    enableReinitialize: false,
    onSubmit: () => undefined,
  });
  const [fieldStates, setFieldStates] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const valuesRef = useRef(formik.values);
  const previousValuesRef = useRef(formik.values);
  const savedValuesRef = useRef(formik.values);
  const saveTimerRef = useRef();
  const savedStateTimersRef = useRef({});
  const saveInProgressRef = useRef(false);
  const saveQueuedRef = useRef(false);
  const executeSaveRef = useRef();
  const mountedRef = useRef(true);
  const onSaveRef = useRef(onSave);
  const incomingInitialValuesRef = useRef(initialValues);
  const formikGetFieldHelpers = formik.getFieldHelpers;
  const formikSetFieldValue = formik.setFieldValue;
  const setFormikValues = formik.setValues;

  valuesRef.current = formik.values;
  onSaveRef.current = onSave;

  const clearSaveTimer = useCallback(() => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = undefined;
  }, []);

  const clearSavedStateTimer = useCallback(path => {
    clearTimeout(savedStateTimersRef.current[path]);
    delete savedStateTimersRef.current[path];
  }, []);

  const setSuccessfulFieldStates = useCallback(
    (submittedPaths, submittedValues) => {
      const currentValues = valuesRef.current;

      setFieldStates(currentStates =>
        submittedPaths.reduce(
          (nextStates, path) => {
            const hasNewerValue = !isEqual(
              getIn(currentValues, path),
              getIn(submittedValues, path),
            );

            if (hasNewerValue) {
              nextStates[path] = "pending";
            } else {
              nextStates[path] = "saved";
            }

            return nextStates;
          },
          { ...currentStates },
        ),
      );

      submittedPaths.forEach(path => {
        clearSavedStateTimer(path);

        if (isEqual(getIn(currentValues, path), getIn(submittedValues, path))) {
          savedStateTimersRef.current[path] = setTimeout(() => {
            if (!mountedRef.current) return;

            setFieldStates(currentStates => {
              if (currentStates[path] !== "saved") return currentStates;

              const nextStates = { ...currentStates };
              delete nextStates[path];
              return nextStates;
            });
            delete savedStateTimersRef.current[path];
          }, savedStateDuration);
        }
      });
    },
    [clearSavedStateTimer, savedStateDuration],
  );

  const setFailedFieldStates = useCallback(
    (submittedPaths, submittedValues) => {
      const currentValues = valuesRef.current;

      setFieldStates(currentStates =>
        submittedPaths.reduce(
          (nextStates, path) => {
            nextStates[path] = isEqual(
              getIn(currentValues, path),
              getIn(submittedValues, path),
            )
              ? "error"
              : "pending";
            return nextStates;
          },
          { ...currentStates },
        ),
      );
    },
    [],
  );

  const executeSave = useCallback(async () => {
    clearSaveTimer();

    if (!mountedRef.current) return;

    if (saveInProgressRef.current) {
      saveQueuedRef.current = true;
      return;
    }

    const submittedValues = valuesRef.current;
    const previousSavedValues = savedValuesRef.current;
    const submittedPaths = getChangedFieldPaths(
      submittedValues,
      previousSavedValues,
    );

    if (submittedPaths.length === 0) {
      setIsSaving(false);
      return;
    }

    saveInProgressRef.current = true;
    setIsSaving(true);
    previousValuesRef.current = submittedValues;
    setFieldStates(currentStates =>
      submittedPaths.reduce(
        (nextStates, path) => ({
          ...nextStates,
          [path]: "saving",
        }),
        currentStates,
      ),
    );

    try {
      await onSaveRef.current?.(submittedValues, previousSavedValues);

      if (!mountedRef.current) return;

      savedValuesRef.current = preserveValuesAtPaths(
        savedValuesRef.current,
        submittedValues,
        submittedPaths,
      );
      setSuccessfulFieldStates(submittedPaths, submittedValues);
    } catch {
      if (mountedRef.current) {
        setFailedFieldStates(submittedPaths, submittedValues);
      }
    } finally {
      saveInProgressRef.current = false;

      if (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        executeSaveRef.current?.();
      } else if (mountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [clearSaveTimer, setFailedFieldStates, setSuccessfulFieldStates]);

  executeSaveRef.current = executeSave;

  const scheduleSave = useCallback(() => {
    clearSaveTimer();
    saveTimerRef.current = setTimeout(executeSave, autosaveDelay);
  }, [autosaveDelay, clearSaveTimer, executeSave]);

  const saveNow = useCallback(async () => {
    clearSaveTimer();
    return executeSave();
  }, [clearSaveTimer, executeSave]);

  const setFieldValue = useCallback(
    (name, value, shouldValidate) => {
      const nextValue =
        typeof value === "function"
          ? value(getIn(valuesRef.current, name))
          : value;
      valuesRef.current = setIn(valuesRef.current, name, nextValue);
      return formikSetFieldValue(name, value, shouldValidate);
    },
    [formikSetFieldValue],
  );

  const getFieldHelpers = useCallback(
    name => ({
      ...formikGetFieldHelpers(name),
      setValue: (value, shouldValidate) =>
        setFieldValue(name, value, shouldValidate),
    }),
    [formikGetFieldHelpers, setFieldValue],
  );

  useEffect(() => {
    if (isEqual(initialValues, incomingInitialValuesRef.current)) return;

    incomingInitialValuesRef.current = initialValues;

    const currentValues = valuesRef.current;
    const unsavedPaths = getChangedFieldPaths(
      currentValues,
      savedValuesRef.current,
    );
    const nextValues = preserveValuesAtPaths(
      initialValues,
      currentValues,
      unsavedPaths,
    );

    savedValuesRef.current = initialValues;
    previousValuesRef.current = nextValues;
    valuesRef.current = nextValues;
    setFormikValues(nextValues, false);
  }, [initialValues, setFormikValues]);

  useEffect(() => {
    const changedPaths = getChangedFieldPaths(
      formik.values,
      savedValuesRef.current,
    );
    const editedPaths = getChangedFieldPaths(
      formik.values,
      previousValuesRef.current,
    );

    previousValuesRef.current = formik.values;

    if (editedPaths.length === 0) return;

    editedPaths.forEach(clearSavedStateTimer);
    setFieldStates(currentStates => {
      const nextStates = { ...currentStates };

      editedPaths.forEach(path => {
        if (changedPaths.includes(path)) {
          nextStates[path] = "pending";
        } else {
          delete nextStates[path];
        }
      });

      return nextStates;
    });

    if (changedPaths.length > 0) {
      scheduleSave();
    } else {
      clearSaveTimer();
    }
  }, [clearSaveTimer, clearSavedStateTimer, formik.values, scheduleSave]);

  useEffect(() => {
    mountedRef.current = true;
    const savedStateTimers = savedStateTimersRef.current;

    return () => {
      mountedRef.current = false;
      clearSaveTimer();
      Object.values(savedStateTimers).forEach(clearTimeout);
    };
  }, [clearSaveTimer]);

  const autosave = useMemo(
    () => {
      const states = Object.values(fieldStates);
      const formState = states.includes("error")
        ? "error"
        : isSaving
          ? "saving"
          : states.includes("pending")
            ? "editing"
            : states.includes("saved")
              ? "saved"
              : "idle";

      return {
        fieldStates,
        formState,
        getFieldState: name => fieldStates[name],
        saveNow,
      };
    },
    [fieldStates, isSaving, saveNow],
  );

  return {
    ...formik,
    autosave,
    getFieldHelpers,
    handleSubmit: event => {
      event?.preventDefault?.();
      return saveNow();
    },
    setFieldValue,
    submitForm: saveNow,
  };
};

export { getChangedFieldPaths, preserveValuesAtPaths };
export default useAutosaveFormik;
